import { useState } from "react"
import useTranslation from "../../hooks/useTranslation"
import { conditionFields, resolveVisibility } from "../../../../common/model/condition/evaluate"
import { resolveElement, conditionalFieldsOf } from "../../../../common/model/condition/resolveElement"
import useConditionValues from "./useConditionValues"
import { FormGroup } from "../form/FormComponents"
import { RelativeWrapper } from "../generic/Layout"
import Triangle from "../generic/Triangle"
import ModalForm from "../modal/ModalForm"

import generateStrings from "../../utils/generateStrings"
import resolveModalForm from "../../utils/model/resolveModalForm"
import { getNestedValue, setNestedValue } from "../../utils/objects/nestedValue"

const runValidation = (validation, state) => {
    const { field, operator, value } = validation

    if (operator === '===') {
        return state[field] === state[value] ? '' : `2px solid red`
    } else return ''
}



const ModalFormElem = ({ item, lineId, validated, section, entity, useStore, path = [], options, origin, subObject }) => {
    const [showModal, setShowModal] = useState(false)
    const { t } = useTranslation()

    // Every hook runs before the visibility gate. The gate used to sit above
    // these three, which was safe only because it was passed `values: {}` and so
    // could read nothing but `$mode` — a constant for the life of the mount.
    // `conditionals` predicates read form fields the user edits, so keeping the
    // gate up there would let the hook count change mid-mount (30.03.02.H §4).
    const conditionKeys = [...new Set([
        ...conditionFields(item?.condition),
        ...conditionalFieldsOf(item),
    ])]
    const conditionValues = useConditionValues(useStore, conditionKeys, lineId, path, item?.subObject || subObject)
    // Get state based on path
    //const [state] = useStore(s => path.length === 0 ? s : getNestedValue(s, path))
    const [state] = useStore(s => s)
    const [localState] = useStore(s => getNestedValue(s, path))

    const sources = { values: conditionValues, context: { mode: options?.mode } }
    item = resolveElement(item, sources)

    //Ignore items that do not match the condition
    if (resolveVisibility(item, sources) !== 'show') return <></>

    const sectionName = section?.id

    let testId = `${sectionName}.${item.control.id}`
    if (lineId !== undefined) testId = `${sectionName}.section.line.${lineId + 1}.${item.control.id}`

    const elemId = lineId !== undefined ? `${item.control.id}-${lineId}` : item.control.id

    // Prefer item-level subObject, then prop-level subObject if provided
    const resolvedSubObject = item?.subObject || subObject

    let border = ''

    if (item.validation) {
        border = runValidation(item.validation, localState)
    }

    //Determines if it needs to load ModalGroups or if it is embedded
    const modalForm = resolveModalForm(item)

    // Base state for this control, respecting path
    const baseContextState = path.length === 0 ? state : getNestedValue(state, path)

    // Determine if control should be disabled based on missing key in state
    const isDisabled = item?.disableOnMissing
        ? (() => {
            const key = item.disableOnMissing;
            if (typeof key === 'string' && key.includes('.')) {
                const parts = key.split('.');
                return !Boolean(getNestedValue(baseContextState, parts));
            } else {
                return !Boolean(baseContextState?.[key]);
            }
        })()
        : false;

    // When subObject is provided (either via item or prop), resolve display context to that sub-object
    const displayContext = resolvedSubObject ? baseContextState?.[resolvedSubObject] : baseContextState

    // Translate all labels before processing.
    const updatedDescriptions = item.descriptions.map(description => {
        if (description.type === 'label') {
            return { ...description, text: t(description.text) }
        }
        return description
    })

    // Always run generateStrings
    let valueField = generateStrings(updatedDescriptions, displayContext)

    // If generateStrings returned an array, flatten it
    if (Array.isArray(valueField)) {
        valueField = valueField.join(' ')
    }

    // Use placeholder only if nothing meaningful was generated
    if (!valueField || valueField.trim() === '' || valueField.trim() === 'n/a') {
        valueField = t(item.control.placeholder)
    }

    const onClickControl = () => {
        setShowModal(true)
    }

    return (
        <>
            <FormGroup name='FormGroup' $ctrlid={item.id} $margin={item.fgMargin} key={item.id}>
                {item.control.label ? <FormGroup.Label htmlFor={elemId}>{t(item.control.label)}</FormGroup.Label> : <></>}
                <RelativeWrapper>
                    <FormGroup.Control
                        id={elemId}
                        type={item.control.type}
                        placeholder={t(item.control.placeholder)}
                        onClick={isDisabled ? undefined : onClickControl}
                        value={valueField}
                        {...(validated && item.required && { required: true })}
                        readOnly
                        disabled={isDisabled}
                        style={{ paddingRight: '2rem', textOverflow: 'ellipsis', border: border, ...(isDisabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}
                        data-testid={testId}
                    />
                    <Triangle />
                </RelativeWrapper>
            </FormGroup>
            {showModal &&
                <ModalForm
                    name='ModalForm'
                    mode='fullscreen'
                    isVisible={showModal}
                    setIsVisible={setShowModal}
                    modalTitle={item.control.placeholder}
                    id={modalForm.id}
                    groups={modalForm.elements}
                buttons={modalForm.buttons}
                submit={modalForm.submit}
                    lineId={lineId}
                    validated={validated}
                    section={section}
                    entity={entity}
                    useStore={useStore}
                    path={path}
                    testId={testId}
                    origin={origin}
                    onSave={item.onSave}
                />
            }
        </>
    )
}

export default ModalFormElem
